import React from 'react'
import Head from 'next/head'
import { useProductState, usePrice, ImageDTO } from '@sentecacommerce-theme/lib'

const generateImage = (images: ImageDTO) => {
  return Object.keys(images || {})
    .map((size) => {
      return `"${images[size].url}"`
    })
    .join(',\n')
}

export const ProductSEO = ({
  favorTitleTemplateOverMeta = false,
  favorDescriptionTemplateOverMeta = false,
  titleTemplate = ':title:',
  descriptionTemplate = ':description:',
}: {
  favorTitleTemplateOverMeta?: boolean
  favorDescriptionTemplateOverMeta?: boolean
  titleTemplate?: string
  descriptionTemplate?: string
} = {}) => {
  const { metaData, seoData, selectedVariant } = useProductState()
  const title = seoData.title || metaData.name || ''
  const description = escape(
    seoData.description ||
      metaData.shortDescription ||
      metaData.description ||
      ''
  )
  const { price } = usePrice({
    price: selectedVariant?.offer?.originalPrice,
  })
  const brandName = escape(metaData.brand?.name || '')
  const categoryName = escape(
    metaData.category?.name || metaData.categories?.[0]?.name || ''
  )
  const image = selectedVariant?.images?.[0]

  const currencyCode = selectedVariant?.offer?.originalPrice?.currencyCode

  const availability =
    (selectedVariant?.offer?.quantity || 0) > 0
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock'

  const itemCondition = 'https://schema.org/NewCondition'

  const createTemplate = (template: string) => {
    return template
      .replace(':title:', title)
      .replace(':name:', metaData.name || '')
      .replace(':description:', description)
      .replace(':price:', price.displayValue)
      .replace(':quantity:', (selectedVariant?.offer?.quantity || 0).toString())
      .replace(':brand:', brandName)
      .replace(':category:', categoryName)
  }

  const formattedDescription = favorDescriptionTemplateOverMeta
    ? createTemplate(descriptionTemplate)
    : seoData.description || createTemplate(descriptionTemplate)

  const formattedTitle = favorTitleTemplateOverMeta
    ? createTemplate(titleTemplate)
    : seoData.title || createTemplate(titleTemplate)

  const productJSONLd = `{
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "${formattedTitle}",
    "image": [${generateImage(image as ImageDTO)}],
    "description": "${formattedDescription}",
    "sku": "${selectedVariant?.sku || ''}",
    ${
      brandName
        ? `"brand": {
          "@type": "Brand",
          "name": "${brandName}"
        },`
        : ''
    }
    ${`"aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "${metaData.reviewRatingStatistics?.averageRating}",
          "reviewCount": "${metaData.reviewRatingStatistics?.count}"
        },`}
    "offers": {
      "@type": "Offer",
      "priceCurrency": "${currencyCode}",
      "price": "${price.value}",
      "itemCondition": "${itemCondition}",
      "availability": "${availability}"
    }
  }`
  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: productJSONLd }}
      />
    </Head>
  )
}
